import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, ChevronLeft, ChevronRight, X, Users, Sparkles, Settings2, Check, CircleDashed, Download } from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fmtDate, todayIstIso } from "@/utils/date";

// LIM Flow's editorial calendar for the Digital Team + Super Admin.
// Every entry is a lightweight "post plan" — a custom label, client, notes,
// and a scheduled date — drag/drop between days to re-plan the month.

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(d, n) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  return x;
}
function startOfWeekMonday(d) {
  const day = d.getDay(); // 0..6, 0 = Sun
  const diff = (day + 6) % 7; // Monday = 0
  return addDays(d, -diff);
}
function monthGridDays(monthDate) {
  // 6 weeks × 7 days, aligned Monday-first.
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = startOfWeekMonday(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

// Cohesive palette. Each client is deterministically mapped to a colour so the
// same client keeps the same colour across days without a config table.
const CLIENT_PALETTE = [
  { bg: "bg-[#E11D2E]/12", border: "border-[#E11D2E]/45", text: "text-[#E11D2E] dark:text-[#ff8a95]", dot: "bg-[#E11D2E]" },
  { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-300", dot: "bg-emerald-500" },
  { bg: "bg-sky-500/10", border: "border-sky-500/40", text: "text-sky-300", dot: "bg-sky-500" },
  { bg: "bg-amber-500/10", border: "border-amber-500/40", text: "text-amber-300", dot: "bg-amber-500" },
  { bg: "bg-rose-500/10", border: "border-rose-500/40", text: "text-rose-300", dot: "bg-rose-500" },
  { bg: "bg-lime-500/10", border: "border-lime-500/40", text: "text-lime-300", dot: "bg-lime-500" },
  { bg: "bg-sky-500/10", border: "border-sky-500/40", text: "text-sky-300", dot: "bg-sky-500" },
  { bg: "bg-orange-500/10", border: "border-orange-500/40", text: "text-orange-300", dot: "bg-orange-500" },
];
function paletteFor(client) {
  let h = 0;
  for (const ch of client || "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CLIENT_PALETTE[h % CLIENT_PALETTE.length];
}

export default function SocialCalendar() {
  const { user } = useAuth();
  const allowed = user?.is_super_admin || user?.is_admin || user?.team === "Digital Team";

  const [view, setView] = useState("month"); // month | week | day
  const [cursor, setCursor] = useState(() => {
    // Anchor to today in IST — otherwise a foreign browser TZ would drift the picker
    const [y, m, d] = todayIstIso().split("-").map(Number);
    return new Date(y, m - 1, d);
  });
  const [posts, setPosts] = useState([]);
  const [clients, setClients] = useState([]);
  const [manageableClients, setManageableClients] = useState([]); // super-admin curated list
  const [clientConfigs, setClientConfigs] = useState([]);         // [{name, monthly_target}]
  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | incomplete | complete
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { mode: "create"|"edit", initial: {} }
  const [clientMgrOpen, setClientMgrOpen] = useState(false);

  // Date bounds sent to the API — we pull an entire month grid regardless of
  // sub-view so switching between Month/Week/Day feels instant (no re-fetch).
  const gridStart = useMemo(() => startOfWeekMonday(new Date(cursor.getFullYear(), cursor.getMonth(), 1)), [cursor]);
  const gridEnd = useMemo(() => addDays(gridStart, 41), [gridStart]);

  const load = async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const params = { date_from: isoOf(gridStart), date_to: isoOf(gridEnd) };
      if (clientFilter !== "all") params.client = clientFilter;
      const [{ data: p }, { data: c }] = await Promise.all([
        api.get("/social-posts", { params }),
        api.get("/social-posts/clients"),
      ]);
      setPosts(p || []);
      setClients(c?.clients || []);
      setManageableClients(c?.manageable || []);
      setClientConfigs(c?.client_configs || []);
    } catch (e) {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [allowed, gridStart.toDateString(), clientFilter]);

  // Map scheduled_date → posts for O(1) day lookup during render
  const filteredPosts = useMemo(() => {
    if (statusFilter === "all") return posts;
    return posts.filter((p) => (p.status || "incomplete") === statusFilter);
  }, [posts, statusFilter]);

  const postsByDate = useMemo(() => {
    const m = new Map();
    filteredPosts.forEach((p) => {
      const arr = m.get(p.scheduled_date) || [];
      arr.push(p);
      m.set(p.scheduled_date, arr);
    });
    // Sort within a day by client then label for stable order
    m.forEach((arr) => arr.sort((a, b) => (a.client || "").localeCompare(b.client) || (a.label || "").localeCompare(b.label)));
    return m;
  }, [filteredPosts]);

  const doneCount = posts.filter((p) => p.status === "complete").length;

  // Per-client month target vs actuals for the *cursor month*. We look at the
  // full calendar month the cursor lives in (not the 42-day grid) so a client
  // with target=8 always compares against their actual month, not the leading /
  // trailing days that spill into the previous/next month view.
  //
  // Splits by post_type: photo (=static) / video / story. Each has its own
  // target/scheduled/complete triplet so managers see the breakdown.
  const monthTargetRows = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const pad = (n) => String(n).padStart(2, "0");
    const monthPrefix = `${y}-${pad(m + 1)}-`;
    const inMonth = posts.filter((p) => (p.scheduled_date || "").startsWith(monthPrefix));
    return clientConfigs.map((cfg) => {
      const cp = inMonth.filter((p) => p.client === cfg.name);
      const bucketOf = (p) => {
        const t = p.post_type || "static";
        if (t === "video") return "video";
        if (t === "story") return "story";
        return "photo";       // static = photo
      };
      const byType = { photo: [], video: [], story: [] };
      cp.forEach((p) => { byType[bucketOf(p)].push(p); });
      const stat = (arr) => ({
        scheduled: arr.length,
        complete: arr.filter((p) => p.status === "complete").length,
      });
      return {
        name: cfg.name,
        photo: { target: cfg.target_photo || 0, ...stat(byType.photo) },
        video: { target: cfg.target_video || 0, ...stat(byType.video) },
        story: { target: cfg.target_story || 0, ...stat(byType.story) },
      };
    }).filter((r) =>
      r.photo.target > 0 || r.video.target > 0 || r.story.target > 0 ||
      r.photo.scheduled > 0 || r.video.scheduled > 0 || r.story.scheduled > 0
    );
  }, [clientConfigs, posts, cursor]);

  const moveTo = (postId, dateISO) => {
    const post = posts.find((p) => p.id === postId);
    if (!post || post.scheduled_date === dateISO) return;
    // Optimistic update — dragging feels instant, revert on server rejection
    const prev = posts;
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, scheduled_date: dateISO } : p)));
    api.patch(`/social-posts/${postId}`, { scheduled_date: dateISO })
      .then(() => toast.success(`Moved to ${fmtDate(dateISO)}`))
      .catch(() => { setPosts(prev); toast.error("Move failed — reverted"); });
  };

  // Toggle complete / incomplete. Any Digital Team member can do this.
  const toggleStatus = (postId) => {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    const next = post.status === "complete" ? "incomplete" : "complete";
    const prev = posts;
    setPosts((cur) => cur.map((p) => (p.id === postId ? { ...p, status: next } : p)));
    api.patch(`/social-posts/${postId}`, { status: next })
      .then(({ data }) => {
        setPosts((cur) => cur.map((p) => (p.id === postId ? data : p)));
        toast.success(next === "complete" ? "Marked complete" : "Marked incomplete");
      })
      .catch(() => { setPosts(prev); toast.error("Update failed"); });
  };

  const saveDialog = async (values, id) => {
    try {
      if (id) {
        const { data } = await api.patch(`/social-posts/${id}`, values);
        setPosts((prev) => prev.map((p) => (p.id === id ? data : p)));
        toast.success("Post updated");
      } else {
        const { data } = await api.post("/social-posts", values);
        setPosts((prev) => [...prev, data]);
        if (!clients.includes(data.client)) setClients((prev) => [...prev, data.client].sort());
        toast.success("Post added");
      }
      setDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/social-posts/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  if (!allowed) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center">
        <CalendarClock size={40} className="mx-auto text-white/40 mb-4" />
        <div className="text-lg">Social Media Calendar is limited to the Digital Team, Admins & Super Admins.</div>
      </div>
    );
  }

  const monthLabel = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;

  const jump = (delta) => {
    if (view === "month") setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
    else if (view === "week") setCursor(addDays(cursor, delta * 7));
    else setCursor(addDays(cursor, delta));
  };
  const goToday = () => {
    const [y, m, d] = todayIstIso().split("-").map(Number);
    setCursor(new Date(y, m - 1, d));
  };
  // Jump the cursor to the Monday of THIS week / LAST week and switch the
  // calendar into "week" mode so the manager sees exactly the range they'll
  // be downloading as a PDF for the Weekly Submission task.
  const jumpToWeek = (offset) => {
    const [y, m, d] = todayIstIso().split("-").map(Number);
    const today = new Date(y, m - 1, d);
    const dow = (today.getDay() + 6) % 7;      // Mon = 0
    const monday = new Date(today);
    monday.setDate(today.getDate() - dow + offset * 7);
    setCursor(monday);
    setView("week");
  };
  // Compute the ISO Mon..Sun range of the current cursor — used both for
  // display + as the PDF endpoint's date_from / date_to query params.
  const cursorWeekRange = (() => {
    const anchor = new Date(cursor);
    const dow = (anchor.getDay() + 6) % 7;
    const monday = new Date(anchor); monday.setDate(anchor.getDate() - dow);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: iso(monday), to: iso(sunday) };
  })();
  const downloadWeekPdf = async (from, to) => {
    const clientParam = clientFilter && clientFilter !== "all" ? `&client=${encodeURIComponent(clientFilter)}` : "";
    try {
      const res = await api.get(`/social-posts/pdf?date_from=${from}&date_to=${to}${clientParam}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `social-calendar-${from}-to-${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download PDF");
    }
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header + view switcher */}
      <div className="flex flex-wrap gap-3 justify-between items-end mb-6">
        <div>
          <div className="overline flex items-center gap-2"><CalendarClock size={12} /> Social Media Calendar</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1">{monthLabel}</h1>
          <p className="text-sm text-white/50 mt-1">Plan the month · drag any post to reschedule · hover for full detail.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-white/10 bg-black/40 p-0.5">
            {["day", "week", "month"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`social-cal-view-${v}`}
                className={`text-xs font-mono uppercase tracking-widest px-3 h-8 rounded-full transition-colors ${
                  view === v ? "bg-[#E11D2E] text-white shadow" : "text-white/60 hover:text-white"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => jump(-1)} data-testid="social-cal-prev" className="w-8 h-8 rounded-full border border-white/10 hover:bg-white/5 flex items-center justify-center"><ChevronLeft size={16} /></button>
            <button onClick={goToday} data-testid="social-cal-today" className="text-xs font-mono uppercase tracking-widest px-3 h-8 rounded-full border border-white/10 hover:bg-white/5">Today</button>
            <button onClick={() => jump(1)} data-testid="social-cal-next" className="w-8 h-8 rounded-full border border-white/10 hover:bg-white/5 flex items-center justify-center"><ChevronRight size={16} /></button>
          </div>
          {/* Quick-nav pills — used by the Digital manager every Monday to pull
              up last-week (annotate what got posted) or this-week (plan). Both
              switch into `week` view + jump cursor to the correct Monday. */}
          <button
            onClick={() => jumpToWeek(-1)}
            data-testid="social-cal-last-week"
            className="text-xs font-mono uppercase tracking-widest px-3 h-8 rounded-full border border-white/10 hover:bg-white/5 text-white/70 hover:text-white"
          >
            Last week
          </button>
          <button
            onClick={() => jumpToWeek(0)}
            data-testid="social-cal-this-week"
            className="text-xs font-mono uppercase tracking-widest px-3 h-8 rounded-full border border-white/10 hover:bg-white/5 text-white/70 hover:text-white"
          >
            This week
          </button>
          <Button
            variant="outline"
            data-testid="social-cal-pdf-btn"
            title={`Download ${cursorWeekRange.from} → ${cursorWeekRange.to} as PDF (colour-coded by post status)`}
            onClick={() => downloadWeekPdf(cursorWeekRange.from, cursorWeekRange.to)}
            className="border-amber-500/40 text-amber-200 hover:border-amber-400 hover:text-white hover:bg-amber-500/10 rounded-full"
          >
            <Download size={13} className="mr-1.5" /> PDF ({cursorWeekRange.from.slice(5)} → {cursorWeekRange.to.slice(5)})
          </Button>
          <Button data-testid="social-cal-new-btn" onClick={() => setDialog({ mode: "create", initial: { scheduled_date: todayIstIso() } })} className="bg-[#E11D2E] hover:bg-[#c1172a] rounded-full">
            <Plus size={14} className="mr-2" /> New post
          </Button>
        </div>
      </div>

      {/* Client filter strip */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="overline mr-1"><Users size={11} className="inline -mt-0.5 mr-1" /> Client</div>
        <button
          onClick={() => setClientFilter("all")}
          data-testid="social-cal-client-all"
          className={`text-xs uppercase tracking-widest font-mono px-3 h-8 rounded-full border ${clientFilter === "all" ? "bg-[#E11D2E]/15 border-[#E11D2E]/40 text-white" : "border-white/10 text-white/60 hover:text-white hover:border-white/25"}`}
        >
          All ({posts.length})
        </button>
        {clients.map((c) => {
          const p = paletteFor(c);
          const clientPosts = posts.filter((x) => x.client === c);
          const videos  = clientPosts.filter((x) => (x.post_type || "static") === "video").length;
          const photos  = clientPosts.filter((x) => (x.post_type || "static") === "static").length;
          const stories = clientPosts.filter((x) => (x.post_type || "static") === "story").length;
          const active = clientFilter === c;
          return (
            <button
              key={c}
              onClick={() => setClientFilter(c)}
              data-testid={`social-cal-client-${c.replace(/\W+/g, "-")}`}
              title={`${c} — 🎬 ${videos} · 🖼 ${photos} · 📱 ${stories} in view`}
              className={`text-xs uppercase tracking-widest font-mono px-3 h-8 rounded-full border inline-flex items-center gap-1.5 transition-colors ${active ? `${p.bg} ${p.border} ${p.text}` : "border-white/10 text-white/60 hover:text-white hover:border-white/25"}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
              {c}
              {(videos > 0 || photos > 0 || stories > 0) && (
                <span
                  className="text-white/50 flex items-center gap-1"
                  data-testid={`social-cal-client-${c.replace(/\W+/g, "-")}-counts`}
                >
                  {" · "}
                  <span className="text-rose-300/80">🎬{videos}</span>
                  <span className="text-sky-300/80">🖼{photos}</span>
                  {stories > 0 && <span className="text-amber-300/80">📱{stories}</span>}
                </span>
              )}
            </button>
          );
        })}
        {user?.is_super_admin && (
          <button
            onClick={() => setClientMgrOpen(true)}
            data-testid="social-cal-manage-clients"
            className="text-xs uppercase tracking-widest font-mono px-3 h-8 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/25 inline-flex items-center gap-1.5"
            title="Add or delete clients (Super Admin only)"
          >
            <Settings2 size={11} /> Manage clients
          </button>
        )}
      </div>

      {/* Status filter + progress */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="overline mr-1">Status</div>
        {[
          { k: "all",        lb: `All (${posts.length})`,                        cls: "bg-[#E11D2E]/15 border-[#E11D2E]/40 text-white" },
          { k: "incomplete", lb: `Incomplete (${posts.length - doneCount})`,     cls: "bg-amber-500/10 border-amber-500/40 text-amber-300" },
          { k: "complete",   lb: `Complete (${doneCount})`,                      cls: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" },
        ].map(({ k, lb, cls }) => (
          <button
            key={k}
            onClick={() => setStatusFilter(k)}
            data-testid={`social-cal-status-${k}`}
            className={`text-xs uppercase tracking-widest font-mono px-3 h-8 rounded-full border ${
              statusFilter === k ? cls : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
            }`}
          >
            {lb}
          </button>
        ))}
        {posts.length > 0 && (
          <span className="ml-auto text-xs font-mono text-white/40">
            {doneCount}/{posts.length} done · {Math.round((doneCount / posts.length) * 100)}%
          </span>
        )}
      </div>

      {/* Monthly target vs actual — one row per client with a target set.
          Filters into the grid on click so a manager can zoom into any lag. */}
      {monthTargetRows.length > 0 && (
        <div className="mb-4 rounded-lg border border-white/10 bg-black/25 p-3" data-testid="social-cal-target-panel">
          <div className="flex items-center justify-between mb-2.5">
            <div className="overline flex items-center gap-1.5">
              <Users size={11} /> Monthly targets — {monthLabel}
            </div>
            <div className="text-[0.7rem] text-white/40 font-mono">tap a client to filter</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {monthTargetRows.map((r) => {
              const isActive = clientFilter === r.name;
              const pal = paletteFor(r.name);
              const totalTarget = r.photo.target + r.video.target + r.story.target;
              const totalScheduled = r.photo.scheduled + r.video.scheduled + r.story.scheduled;
              const totalComplete = r.photo.complete + r.video.complete + r.story.complete;
              const SubRow = ({ label, icon, data, dot }) => {
                if (!data.target && !data.scheduled) return null;
                const pctS = data.target > 0 ? Math.min(100, Math.round((data.scheduled / data.target) * 100)) : 0;
                const pctC = data.target > 0 ? Math.min(100, Math.round((data.complete  / data.target) * 100)) : 0;
                const shortfall = data.target > 0 ? Math.max(0, data.target - data.scheduled) : 0;
                return (
                  <div className="flex items-center gap-2 text-[0.7rem] font-mono">
                    <span className="w-14 shrink-0 text-white/50 uppercase tracking-widest">
                      {icon} {label}
                    </span>
                    <div className="relative h-1.5 rounded-full bg-white/5 overflow-hidden flex-1 min-w-[60px]">
                      {data.target > 0 && (
                        <>
                          <div className={`absolute inset-y-0 left-0 ${dot} opacity-30`} style={{ width: `${pctS}%` }} />
                          <div className={`absolute inset-y-0 left-0 ${dot}`} style={{ width: `${pctC}%` }} />
                        </>
                      )}
                    </div>
                    <span className="shrink-0 text-white/70 w-14 text-right">
                      {data.scheduled}<span className="text-white/30">/</span>{data.target || "—"}
                    </span>
                    <span className={`shrink-0 w-10 text-right ${data.target > 0 && shortfall === 0 ? "text-emerald-300/80" : shortfall > 0 ? "text-amber-300/80" : "text-white/30"}`}>
                      {data.target > 0 ? (shortfall > 0 ? `-${shortfall}` : "✓") : "—"}
                    </span>
                  </div>
                );
              };
              return (
                <button
                  key={r.name}
                  onClick={() => setClientFilter(isActive ? "all" : r.name)}
                  data-testid={`social-cal-target-row-${r.name.replace(/\W+/g, "-")}`}
                  className={`text-left rounded-md border p-2.5 transition ${
                    isActive ? `${pal.bg} ${pal.border}` : "border-white/10 bg-black/30 hover:border-white/25"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${pal.dot}`} />
                      <span className="text-xs font-medium truncate">{r.name}</span>
                    </div>
                    <div className="shrink-0 text-[0.7rem] font-mono text-white/50">
                      total {totalScheduled}<span className="text-white/30">/</span>{totalTarget || "—"} · {totalComplete}✓
                    </div>
                  </div>
                  <div className="space-y-1">
                    <SubRow label="Static" icon="🖼" data={r.photo} dot={pal.dot} />
                    <SubRow label="Video" icon="🎬" data={r.video} dot={pal.dot} />
                    <SubRow label="Story" icon="📱" data={r.story} dot={pal.dot} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Body: depends on view */}
      {loading && posts.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center text-white/40">Loading…</div>
      ) : view === "month" ? (
        <MonthGrid cursor={cursor} postsByDate={postsByDate} onEdit={(p) => setDialog({ mode: "edit", initial: p })} onCreate={(d) => setDialog({ mode: "create", initial: { scheduled_date: d } })} moveTo={moveTo} onDelete={deletePost} onToggle={toggleStatus} />
      ) : view === "week" ? (
        <WeekStrip cursor={cursor} postsByDate={postsByDate} onEdit={(p) => setDialog({ mode: "edit", initial: p })} onCreate={(d) => setDialog({ mode: "create", initial: { scheduled_date: d } })} moveTo={moveTo} onDelete={deletePost} onToggle={toggleStatus} />
      ) : (
        <DayList cursor={cursor} postsByDate={postsByDate} onEdit={(p) => setDialog({ mode: "edit", initial: p })} onCreate={(d) => setDialog({ mode: "create", initial: { scheduled_date: d } })} onDelete={deletePost} onToggle={toggleStatus} />
      )}

      {dialog && (
        <PostDialog
          mode={dialog.mode}
          initial={dialog.initial}
          knownClients={clients}
          onClose={() => setDialog(null)}
          onSave={(vals) => saveDialog(vals, dialog.initial?.id)}
          onDelete={() => { deletePost(dialog.initial.id); setDialog(null); }}
        />
      )}
      {clientMgrOpen && (
        <ClientManagerDialog
          clients={clients}
          manageable={manageableClients}
          clientConfigs={clientConfigs}
          posts={posts}
          onClose={() => setClientMgrOpen(false)}
          onAdd={async (name, targets) => {
            try {
              await api.post("/social-posts/clients", { name, ...targets });
              toast.success("Client added");
              await load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Failed to add client");
            }
          }}
          onUpdateTarget={async (name, targets) => {
            try {
              await api.patch(`/social-posts/clients/${encodeURIComponent(name)}`, targets);
              toast.success(`Targets updated for ${name}`);
              await load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Failed to update target");
            }
          }}
          onDelete={async (name, force) => {
            try {
              const { data } = await api.delete(`/social-posts/clients/${encodeURIComponent(name)}${force ? "?force=true" : ""}`);
              toast.success(force && data?.posts_deleted
                ? `Deleted ${name} and ${data.posts_deleted} post${data.posts_deleted === 1 ? "" : "s"}`
                : `Deleted ${name}`);
              if (clientFilter === name) setClientFilter("all");
              await load();
              return true;
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Delete failed");
              return false;
            }
          }}
        />
      )}
    </div>
  );
}

// -------------------------- Sub-views --------------------------

function DayCell({ dateISO, dateLabel, posts, isToday, isOtherMonth, onCreate, onEdit, onDropPost, onDelete, onToggle, compact = false }) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        const id = e.dataTransfer.getData("text/social-post-id");
        if (id) onDropPost(id, dateISO);
      }}
      className={`relative flex flex-col ${compact ? "min-h-[110px]" : "min-h-[140px]"} p-2 border border-white/5 bg-black/30 ${
        isOtherMonth ? "opacity-50" : ""
      } ${isToday ? "ring-1 ring-[#E11D2E]/40" : ""} ${dragOver ? "bg-[#E11D2E]/10" : ""} rounded-md transition-colors group`}
      data-testid={`social-cal-cell-${dateISO}`}
    >
      <div className="flex items-center justify-between text-[0.7rem] font-mono uppercase text-white/40">
        <span className={`${isToday ? "text-[#E11D2E]" : ""}`}>{dateLabel}</span>
        <button
          onClick={() => onCreate(dateISO)}
          className="opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 rounded-full border border-white/15 hover:bg-white/10 flex items-center justify-center"
          title="Add post"
          data-testid={`social-cal-add-${dateISO}`}
        >
          <Plus size={10} />
        </button>
      </div>
      <div className="flex flex-col gap-1 mt-1 overflow-hidden">
        {(posts || []).map((p) => (
          <PostChip key={p.id} post={p} onEdit={() => onEdit(p)} onDelete={() => onDelete(p.id)} onToggle={() => onToggle(p.id)} />
        ))}
      </div>
    </div>
  );
}

function PostChip({ post, onEdit, onDelete, onToggle }) {
  const p = paletteFor(post.client);
  const done = post.status === "complete";
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/social-post-id", post.id)}
      onClick={onEdit}
      data-testid={`social-cal-post-${post.id}`}
      data-status={post.status || "incomplete"}
      className={`group/chip relative rounded-md border ${p.border} ${done ? "bg-emerald-500/10 border-emerald-500/40" : p.bg} px-2 py-1 cursor-grab active:cursor-grabbing hover:brightness-125 transition-all`}
      title={`${post.client} — ${post.label}${post.notes ? `\n\n${post.notes}` : ""}`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          data-testid={`social-cal-post-toggle-${post.id}`}
          title={done ? "Mark incomplete" : "Mark complete"}
          className={`w-3.5 h-3.5 rounded-sm border shrink-0 flex items-center justify-center transition-colors ${
            done
              ? "bg-emerald-500 border-emerald-500 text-white"
              : "border-white/40 hover:border-emerald-400"
          }`}
        >
          {done && <Check size={9} strokeWidth={3} />}
        </button>
        <span className={`text-xs leading-tight font-medium truncate ${done ? "line-through text-white/50" : p.text}`}>
          <span className="mr-1" title={
            (post.post_type || "static") === "video" ? "Video" :
            (post.post_type || "static") === "story" ? "Story" : "Static"
          }>
            {(post.post_type || "static") === "video" ? "🎬" :
             (post.post_type || "static") === "story" ? "📱" : "🖼"}
          </span>
          {post.label}
        </span>
      </div>
      <div className={`text-[0.65rem] uppercase tracking-widest ${done ? "text-white/40" : p.text} opacity-60 truncate pl-5`}>{post.client}</div>
      {/* Hover preview card */}
      <div className="absolute z-20 left-0 top-full mt-1 w-64 opacity-0 pointer-events-none group-hover/chip:opacity-100 group-hover/chip:pointer-events-auto transition-opacity">
        <div className="rounded-lg border border-white/15 bg-surface shadow-xl p-3">
          <div className="flex items-center justify-between">
            <div className={`text-[0.7rem] uppercase tracking-widest ${p.text}`}>{post.client}</div>
            <span className={`text-[0.65rem] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${
              done ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"
            }`}>
              {done ? "Complete" : "Incomplete"}
            </span>
          </div>
          <div className={`text-sm font-medium mt-0.5 ${done ? "line-through text-white/50" : ""}`}>{post.label}</div>
          {post.notes && <div className="text-xs text-white/60 mt-1.5 whitespace-pre-wrap">{post.notes}</div>}
          <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between text-[0.7rem] text-white/40 font-mono">
            <span>{done && post.completed_by_name ? `Done by ${post.completed_by_name}` : (post.updated_by_name || post.created_by_name)}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-red-400 hover:text-red-300"
              data-testid={`social-cal-post-delete-${post.id}`}
            >
              <Trash2 size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MonthGrid({ cursor, postsByDate, onEdit, onCreate, moveTo, onDelete, onToggle }) {
  const days = monthGridDays(cursor);
  const todayIso = todayIstIso();
  return (
    <div>
      <div className="grid grid-cols-7 mb-2 text-[0.7rem] uppercase tracking-widest text-white/40 font-mono">
        {WEEKDAY_SHORT.map((w) => <div key={w} className="px-2 py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const iso = isoOf(d);
          return (
            <DayCell
              key={iso}
              dateISO={iso}
              dateLabel={String(d.getDate())}
              posts={postsByDate.get(iso)}
              isToday={iso === todayIso}
              isOtherMonth={d.getMonth() !== cursor.getMonth()}
              onCreate={onCreate}
              onEdit={onEdit}
              onDropPost={moveTo}
              onDelete={onDelete}
              onToggle={onToggle}
              compact
            />
          );
        })}
      </div>
    </div>
  );
}

function WeekStrip({ cursor, postsByDate, onEdit, onCreate, moveTo, onDelete, onToggle }) {
  const start = startOfWeekMonday(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const todayIso = todayIstIso();
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
      {days.map((d) => {
        const iso = isoOf(d);
        return (
          <div key={iso}>
            <div className="text-[0.7rem] uppercase tracking-widest text-white/40 font-mono px-1 mb-1">
              {d.toLocaleDateString("en-IN", { weekday: "short" })} · {d.getDate()}/{d.getMonth() + 1}
            </div>
            <DayCell
              dateISO={iso}
              dateLabel={String(d.getDate())}
              posts={postsByDate.get(iso)}
              isToday={iso === todayIso}
              isOtherMonth={false}
              onCreate={onCreate}
              onEdit={onEdit}
              onDropPost={moveTo}
              onDelete={onDelete}
              onToggle={onToggle}
            />
          </div>
        );
      })}
    </div>
  );
}

function DayList({ cursor, postsByDate, onEdit, onCreate, onDelete, onToggle }) {
  const iso = isoOf(cursor);
  const posts = postsByDate.get(iso) || [];
  // Group by client for a clean daily plan
  const byClient = new Map();
  posts.forEach((p) => {
    const arr = byClient.get(p.client) || [];
    arr.push(p);
    byClient.set(p.client, arr);
  });
  return (
    <div className="glass rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline">Daily plan</div>
          <div className="text-xl font-display mt-1">{fmtDate(iso)} · {cursor.toLocaleDateString("en-IN", { weekday: "long" })}</div>
        </div>
        <Button onClick={() => onCreate(iso)} data-testid={`social-cal-day-add-${iso}`} className="bg-[#E11D2E] hover:bg-[#c1172a] rounded-full">
          <Plus size={14} className="mr-2" /> Add post
        </Button>
      </div>
      {posts.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-lg p-8 text-center text-white/40" data-testid="social-cal-day-empty">
          Nothing planned for this day.
        </div>
      ) : (
        <div className="space-y-6">
          {[...byClient.entries()].map(([client, list]) => {
            const p = paletteFor(client);
            return (
              <div key={client}>
                <div className={`overline flex items-center gap-2 ${p.text} mb-2`}>
                  <span className={`w-2 h-2 rounded-full ${p.dot}`} /> {client} · {list.length} post{list.length === 1 ? "" : "s"}
                </div>
                <div className="space-y-2">
                  {list.map((post) => {
                    const done = post.status === "complete";
                    return (
                      <div
                        key={post.id}
                        onClick={() => onEdit(post)}
                        data-testid={`social-cal-day-post-${post.id}`}
                        data-status={post.status || "incomplete"}
                        className={`rounded-lg border p-3 cursor-pointer hover:brightness-125 transition-all group ${
                          done ? "bg-emerald-500/10 border-emerald-500/40" : `${p.border} ${p.bg}`
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); onToggle(post.id); }}
                              data-testid={`social-cal-day-toggle-${post.id}`}
                              title={done ? "Mark incomplete" : "Mark complete"}
                              className={`w-5 h-5 rounded-sm border shrink-0 flex items-center justify-center mt-0.5 transition-colors ${
                                done ? "bg-emerald-500 border-emerald-500 text-white" : "border-white/40 hover:border-emerald-400"
                              }`}
                            >
                              {done ? <Check size={13} strokeWidth={3} /> : <CircleDashed size={11} className="text-white/40" />}
                            </button>
                            <div className="min-w-0 flex-1">
                              <div className={`font-medium ${done ? "line-through text-white/50" : ""}`}>{post.label}</div>
                              {post.notes && <div className="text-xs text-white/60 mt-1 whitespace-pre-wrap">{post.notes}</div>}
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); onDelete(post.id); }}
                            data-testid={`social-cal-day-delete-${post.id}`}
                            className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-400"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                        <div className="text-[0.7rem] uppercase tracking-widest text-white/30 font-mono mt-2 pl-8">
                          {done && post.completed_by_name ? `Done by ${post.completed_by_name}` : `by ${post.updated_by_name || post.created_by_name}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -------------------------- Dialog --------------------------

function PostDialog({ mode, initial, knownClients, onClose, onSave, onDelete }) {
  const [client, setClient] = useState(initial?.client || "");
  const [label, setLabel] = useState(initial?.label || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [date, setDate] = useState(initial?.scheduled_date || todayIstIso());
  const [status, setStatus] = useState(initial?.status || "incomplete");
  // Content format — Video (reel / long-form / ad film) vs Static (image / carousel / poster).
  const [postType, setPostType] = useState(initial?.post_type || "static");

  const submit = (e) => {
    e.preventDefault();
    if (!client.trim() || !label.trim() || !date) {
      toast.error("Client, label and date are required");
      return;
    }
    onSave({ client: client.trim(), label: label.trim(), notes: notes.trim(), scheduled_date: date, status, post_type: postType });
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-[2px] backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-surface p-6 shadow-2xl"
        data-testid="social-cal-dialog"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline flex items-center gap-2"><Sparkles size={11} /> Social Media post</div>
            <div className="text-xl font-display mt-1">{mode === "create" ? "New post" : "Edit post"}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white" data-testid="social-cal-dialog-close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="overline">Client</Label>
            {knownClients.length === 0 ? (
              <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs px-3 py-2">
                No clients yet. Ask a Super Admin to add one via <b>Manage clients</b>.
              </div>
            ) : (
              <select
                value={client}
                onChange={(e) => setClient(e.target.value)}
                data-testid="social-cal-dialog-client"
                className="w-full mt-2 h-10 px-3 rounded-md bg-black/40 border border-white/10 text-sm text-white focus:outline-none focus:ring-1 focus:ring-red-500"
                required
              >
                <option value="" disabled>Select a client…</option>
                {knownClients.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <Label className="overline">Label (what has to be posted)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Reel — Founder Q&amp;A"
              data-testid="social-cal-dialog-label"
              className="bg-black/40 border-white/10 h-10 mt-2"
              required
            />
          </div>

          <div>
            <Label className="overline">Notes (optional — shows on hover)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Caption ideas, reference links, hashtags…"
              rows={4}
              data-testid="social-cal-dialog-notes"
              className="bg-black/40 border-white/10 mt-2 resize-none"
            />
          </div>

          <div>
            <Label className="overline">Format</Label>
            <div className="flex items-center gap-2 mt-2" data-testid="social-cal-dialog-type">
              <button
                type="button"
                data-testid="social-cal-dialog-type-video"
                onClick={() => setPostType("video")}
                className={`flex-1 h-10 px-3 rounded-md border text-xs font-mono uppercase tracking-widest transition ${
                  postType === "video"
                    ? "bg-rose-500/15 border-rose-500/50 text-rose-200"
                    : "border-white/10 text-white/50 hover:text-white hover:border-white/25"
                }`}
              >🎬 Video</button>
              <button
                type="button"
                data-testid="social-cal-dialog-type-static"
                onClick={() => setPostType("static")}
                className={`flex-1 h-10 px-3 rounded-md border text-xs font-mono uppercase tracking-widest transition ${
                  postType === "static"
                    ? "bg-sky-500/15 border-sky-500/50 text-sky-200"
                    : "border-white/10 text-white/50 hover:text-white hover:border-white/25"
                }`}
              >🖼 Static</button>
              <button
                type="button"
                data-testid="social-cal-dialog-type-story"
                onClick={() => setPostType("story")}
                className={`flex-1 h-10 px-3 rounded-md border text-xs font-mono uppercase tracking-widest transition ${
                  postType === "story"
                    ? "bg-amber-500/15 border-amber-500/50 text-amber-200"
                    : "border-white/10 text-white/50 hover:text-white hover:border-white/25"
                }`}
              >📱 Story</button>
            </div>
          </div>

          <div>
            <Label className="overline">Scheduled date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="social-cal-dialog-date"
              className="bg-black/40 border-white/10 h-10 mt-2"
              required
            />
          </div>

          <div>
            <Label className="overline">Status</Label>
            <div className="flex gap-2 mt-2">
              {[
                { key: "incomplete", label: "Incomplete", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
                { key: "complete",   label: "Complete",   cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
              ].map(({ key, label: lb, cls }) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => setStatus(key)}
                  data-testid={`social-cal-dialog-status-${key}`}
                  className={`text-xs font-mono uppercase tracking-widest px-3 h-9 rounded-full border transition-colors ${
                    status === key ? cls : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
                  }`}
                >
                  {status === key && key === "complete" ? <Check size={11} className="inline -mt-0.5 mr-1" /> : null}
                  {lb}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          {mode === "edit" ? (
            <button type="button" onClick={onDelete} data-testid="social-cal-dialog-delete" className="text-xs uppercase tracking-widest text-red-400 hover:text-red-300 inline-flex items-center gap-1.5">
              <Trash2 size={12} /> Delete
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="social-cal-dialog-cancel" className="rounded-full">Cancel</Button>
            <Button type="submit" data-testid="social-cal-dialog-save" className="bg-[#E11D2E] hover:bg-[#c1172a] rounded-full">
              {mode === "create" ? "Add to calendar" : "Save changes"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

// -------------------------- Client manager (Super Admin) --------------------------

function ClientManagerDialog({ clients, manageable, clientConfigs = [], posts, onClose, onAdd, onUpdateTarget, onDelete }) {
  // Split the visible list into two buckets:
  //   • Managed = clients the super-admin explicitly added → can be deleted freely.
  //   • Auto-detected = clients only present because a post or task mentions them →
  //     deleting these is destructive (would need force), so we show that path clearly.
  const managedSet = new Set(manageable.map((s) => s.toLowerCase()));
  const targetsByName = useMemo(() => {
    const m = {};
    clientConfigs.forEach((c) => {
      m[c.name.toLowerCase()] = {
        photo: c.target_photo || 0,
        video: c.target_video || 0,
        story: c.target_story || 0,
      };
    });
    return m;
  }, [clientConfigs]);
  const [newName, setNewName] = useState("");
  const [newTargets, setNewTargets] = useState({ photo: "", video: "", story: "" });
  const [busy, setBusy] = useState(false);
  // Per-client draft edits keyed by "<name>|<type>"
  const [drafts, setDrafts] = useState({});

  const asInt = (raw) => {
    const s = String(raw ?? "").trim().replace(",", ".");
    if (s === "") return 0;
    const n = Math.floor(parseFloat(s));
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    await onAdd(name, {
      target_photo: asInt(newTargets.photo),
      target_video: asInt(newTargets.video),
      target_story: asInt(newTargets.story),
    });
    setNewName("");
    setNewTargets({ photo: "", video: "", story: "" });
    setBusy(false);
  };

  const saveTarget = async (name, kind) => {
    const key = `${name}|${kind}`;
    const raw = drafts[key];
    if (raw === undefined || raw === "") return;
    const t = asInt(raw);
    const current = (targetsByName[name.toLowerCase()] || {})[kind] || 0;
    if (t === current) {
      setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      return;
    }
    await onUpdateTarget?.(name, { [`target_${kind}`]: t });
    setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
  };

  const askDelete = async (name) => {
    const linkedCount = posts.filter((p) => p.client === name).length;
    if (linkedCount === 0) {
      if (!window.confirm(`Delete client "${name}"?`)) return;
      await onDelete(name, false);
      return;
    }
    // Confirmed force-delete path — clearly spelled out
    const ok = window.confirm(
      `"${name}" is used by ${linkedCount} scheduled post${linkedCount === 1 ? "" : "s"}.\n\n` +
      "Deleting the client will ALSO delete every scheduled post under it. This cannot be undone.\n\n" +
      "Proceed?"
    );
    if (!ok) return;
    await onDelete(name, true);
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-[2px] backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-surface p-6 shadow-2xl"
        data-testid="social-cal-client-manager"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline flex items-center gap-2"><Users size={11} /> Client roster</div>
            <div className="text-xl font-display mt-1">Manage clients</div>
            <div className="text-xs text-white/50 mt-1">Add or delete clients used on the Social Calendar. (Super Admin only.)</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="social-cal-client-manager-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submitAdd} className="mb-6">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="text-[0.65rem] uppercase tracking-widest text-white/40">Client name</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Panchali Chandrashekhar"
                data-testid="social-cal-new-client-input"
                className="bg-black/40 border-white/10 h-10 mt-1"
              />
            </div>
            {[
              { k: "photo", lb: "🖼 Static" },
              { k: "video", lb: "🎬 Video" },
              { k: "story", lb: "📱 Story" },
            ].map(({ k, lb }) => (
              <div key={k} className="w-20">
                <label className="text-[0.65rem] uppercase tracking-widest text-white/40">{lb}</label>
                <Input
                  value={newTargets[k]}
                  onChange={(e) => setNewTargets((t) => ({ ...t, [k]: e.target.value }))}
                  placeholder="0"
                  inputMode="numeric"
                  data-testid={`social-cal-new-client-target-${k}`}
                  className="bg-black/40 border-white/10 h-10 mt-1 text-center"
                />
              </div>
            ))}
            <Button type="submit" disabled={busy || !newName.trim()} data-testid="social-cal-new-client-add" className="bg-[#E11D2E] hover:bg-[#c1172a] rounded-full h-10">
              <Plus size={14} className="mr-1.5" /> Add
            </Button>
          </div>
          <div className="text-[0.7rem] text-white/40 mt-1.5">
            Targets are per calendar month. Leave any blank if that format isn't in scope for the client.
          </div>
        </form>

        <div className="overline mb-2">Existing clients ({clients.length})</div>
        {clients.length === 0 ? (
          <div className="text-xs text-white/40 italic">No clients yet — add the first one above.</div>
        ) : (
          <div className="max-h-72 overflow-y-auto pr-1 space-y-1.5">
            {clients.map((c) => {
              const linked = posts.filter((p) => p.client === c).length;
              const isManaged = managedSet.has(c.toLowerCase());
              const p = paletteFor(c);
              const current = targetsByName[c.toLowerCase()] || { photo: 0, video: 0, story: 0 };
              return (
                <div
                  key={c}
                  className="rounded-md border border-white/5 hover:bg-white/[0.03] px-2 py-2"
                  data-testid={`social-cal-client-row-${c.replace(/\W+/g, "-")}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${p.dot}`} />
                      <span className="text-sm truncate">{c}</span>
                      {!isManaged && (
                        <span className="text-[0.65rem] uppercase tracking-widest text-white/30 border border-white/10 rounded-full px-1.5 py-0.5">
                          auto-detected
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-white/40 font-mono whitespace-nowrap">
                        {linked} post{linked === 1 ? "" : "s"}
                      </span>
                      <button
                        onClick={() => askDelete(c)}
                        data-testid={`social-cal-delete-client-${c.replace(/\W+/g, "-")}`}
                        className="text-white/40 hover:text-red-400 p-1"
                        title="Delete client"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {isManaged && (
                    <div className="mt-1.5 flex items-center gap-2 pl-4">
                      <span className="text-[0.65rem] uppercase tracking-widest text-white/30 shrink-0">Targets / mo</span>
                      {[
                        { k: "photo", lb: "🖼", tt: "Static" },
                        { k: "video", lb: "🎬", tt: "Video" },
                        { k: "story", lb: "📱", tt: "Story" },
                      ].map(({ k, lb, tt }) => {
                        const draftKey = `${c}|${k}`;
                        const draft = drafts[draftKey];
                        const isDirty = draft !== undefined && String(draft) !== String(current[k]);
                        return (
                          <div key={k} className="flex items-center gap-1" title={`${tt} target per month`}>
                            <span className="text-[0.7rem]">{lb}</span>
                            <Input
                              value={draft !== undefined ? draft : (current[k] || "")}
                              onChange={(e) => setDrafts((d) => ({ ...d, [draftKey]: e.target.value }))}
                              onBlur={() => { if (isDirty) saveTarget(c, k); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveTarget(c, k); } }}
                              placeholder="—"
                              inputMode="numeric"
                              data-testid={`social-cal-client-target-${k}-${c.replace(/\W+/g, "-")}`}
                              className={`bg-black/40 border-white/10 h-7 w-12 text-xs px-1.5 text-center ${isDirty ? "border-emerald-500/50" : ""}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

